import { useState, useRef, FormEvent } from 'react'
import { router } from '@inertiajs/react'
import { useTranslation } from 'react-i18next'
import { AdminLayout } from '../../../layouts/admin-layout'
import { AppHeader } from '../../../components/app-header'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../../components/ui/card'
import { Input } from '../../../components/ui/input'
import { Textarea } from '../../../components/ui/textarea'
import { Button } from '../../../components/ui/button'
import { Label } from '../../../components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select'
import { ProjectSearchPopover } from './ProjectSearchPopover'
import { IconPicker } from './IconPicker'
import { ImagePlus, X } from 'lucide-react'

interface TemplateData {
  id: number
  key: string
  name: string
  description: string
  category: string
  icon: string
  source_repo: string
  is_default: boolean
  active: boolean
  screenshot_url: string | null
}

interface AdminTemplateEditProps {
  template: TemplateData
  auth: {
    user: {
      id: number
      name: string
      email: string
      admin: boolean
      super_admin?: boolean
    }
  }
  flash?: {
    success?: string
    error?: string
  }
}

function Edit({ template }: AdminTemplateEditProps) {
  const { t } = useTranslation('admin')
  const [formData, setFormData] = useState({
    name: template.name,
    description: template.description || '',
    category: template.category,
    icon: template.icon,
    source_repo: template.source_repo,
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [screenshotFile, setScreenshotFile] = useState<File | null>(null)
  const [screenshotPreview, setScreenshotPreview] = useState<string | null>(null)
  const [removeScreenshot, setRemoveScreenshot] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleScreenshotSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setScreenshotFile(file)
    setScreenshotPreview(URL.createObjectURL(file))
  }

  const clearScreenshot = () => {
    setScreenshotFile(null)
    if (screenshotPreview) {
      URL.revokeObjectURL(screenshotPreview)
      setScreenshotPreview(null)
    }
    if (fileInputRef.current) fileInputRef.current.value = ''
    if (template.screenshot_url) {
      setRemoveScreenshot(true)
    }
  }

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)

    if (screenshotFile) {
      router.patch(`/admin/templates/${template.id}`, {
        template: { ...formData, screenshot: screenshotFile },
      } as never, {
        onFinish: () => setIsSubmitting(false),
        forceFormData: true,
      })
    } else {
      router.patch(`/admin/templates/${template.id}`, {
        template: { ...formData, ...(removeScreenshot ? { remove_screenshot: 'true' } : {}) },
      }, {
        onFinish: () => setIsSubmitting(false),
      })
    }
  }

  const currentScreenshot = screenshotPreview || (removeScreenshot ? null : template.screenshot_url)

  return (
    <>
      <AppHeader breadcrumbs={[
        { label: 'Admin', href: '/admin' },
        { label: t('templates.title'), href: '/admin/templates' },
        { label: t('templates.edit.title') },
      ]} />

      <div className="flex flex-1 flex-col">
        <div className="@container/main flex flex-1 flex-col gap-2">
          <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
            <div className="px-4 lg:px-6">
              <div className="max-w-2xl mx-auto">
                <div className="mb-6">
                  <h1 className="text-3xl font-bold text-foreground">{t('templates.edit.title')}</h1>
                  <p className="mt-2 text-sm text-muted-foreground">
                    {t('templates.edit.description', { name: template.name })}
                  </p>
                </div>

                <Card className="shadow-none">
                  <CardHeader>
                    <CardTitle>{t('templates.edit.details')}</CardTitle>
                    <CardDescription>{t('templates.edit.detailsDescription')}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="name">{t('templates.form.name')}</Label>
                        <Input
                          id="name"
                          type="text"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          required
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="description">{t('templates.form.description')}</Label>
                        <Textarea
                          id="description"
                          value={formData.description}
                          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                          rows={3}
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label>{t('templates.form.category')}</Label>
                          <Select
                            value={formData.category}
                            onValueChange={(value) => setFormData({ ...formData, category: value })}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="starter">{t('templates.categories.starter')}</SelectItem>
                              <SelectItem value="business">{t('templates.categories.business')}</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-2">
                          <Label>{t('templates.form.icon')}</Label>
                          <IconPicker
                            value={formData.icon}
                            onValueChange={(value) => setFormData({ ...formData, icon: value })}
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label>{t('templates.form.screenshot')}</Label>
                        {currentScreenshot ? (
                          <div className="relative rounded-lg border overflow-hidden">
                            <img
                              src={currentScreenshot}
                              alt="Template screenshot"
                              className="w-full max-h-48 object-cover object-top"
                            />
                            <Button
                              type="button"
                              variant="destructive"
                              size="icon"
                              className="absolute top-2 right-2 h-6 w-6"
                              onClick={clearScreenshot}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          </div>
                        ) : null}
                        <div className="flex gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => fileInputRef.current?.click()}
                          >
                            <ImagePlus className="h-4 w-4 mr-2" />
                            {currentScreenshot ? t('templates.form.changeScreenshot') : t('templates.form.uploadScreenshot')}
                          </Button>
                        </div>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          onChange={handleScreenshotSelect}
                          className="hidden"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="source_repo">{t('templates.form.sourceRepo')}</Label>
                        <div className="flex gap-2">
                          <Input
                            id="source_repo"
                            type="text"
                            value={formData.source_repo}
                            onChange={(e) => setFormData({ ...formData, source_repo: e.target.value })}
                            required
                          />
                          <ProjectSearchPopover
                            onSelect={(name) => setFormData({ ...formData, source_repo: name })}
                          />
                        </div>
                        <p className="text-xs text-muted-foreground">{t('templates.form.sourceRepoHelp')}</p>
                      </div>

                      <div className="flex justify-end gap-3 pt-6">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => router.visit('/admin/templates')}
                          disabled={isSubmitting}
                        >
                          {t('templates.form.cancel')}
                        </Button>
                        <Button type="submit" disabled={isSubmitting}>
                          {isSubmitting ? t('templates.form.saving') : t('templates.form.save')}
                        </Button>
                      </div>
                    </form>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

Edit.layout = (page: React.ReactNode) => <AdminLayout>{page}</AdminLayout>

export default Edit
